import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isDefaultState, readListState, writeListState, type ListSchema, type ListValues } from './listState';

export interface ListStateControls {
  /** Alle Filter auf Standard */
  reset(): void;
  isDefault: boolean;
}

export interface UseListStateOptions {
  /**
   * false = Zustand nur im Speicher statt in der Adresse. Für Listen, die in ein Dokument
   * eingebettet sind und deren Parameter mit denen der Seite kollidieren würden.
   */
  enabled?: boolean;
}

/**
 * Listenzustand in der Adresse (Regel „Dokument-Navigation" R2). Geschrieben wird immer
 * ersetzend: Filterwechsel erzeugen keinen Verlaufseintrag, und „Zurück" aus dem Dokument
 * trifft die Liste mit ihren Filtern.
 *
 * Das Schema gehört auf Modul-Ebene (außerhalb der Komponente), damit es stabil bleibt.
 */
export function useListState<S extends ListSchema>(
  schema: S,
  opts: UseListStateOptions = {},
): [ListValues<S>, (patch: Partial<ListValues<S>>) => void, ListStateControls] {
  const enabled = opts.enabled !== false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [local, setLocal] = useState<URLSearchParams>(() => new URLSearchParams());

  const source = enabled ? searchParams : local;
  const sourceKey = source.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const values = useMemo(() => readListState(schema, source), [schema, sourceKey]);

  const set = useCallback((patch: Partial<ListValues<S>>) => {
    if (enabled) {
      // Vom LIVE-Stand der Adresse ausgehen, nicht vom Render-Stand: React Router reicht dem
      // Funktions-Updater die Parameter des letzten Renderns. Zwei Aufrufe im selben Ereignis
      // (z. B. Reiter wechseln + Suche leeren) überschrieben sich sonst gegenseitig — die
      // Reiter-Leiste des Adressbuchs „hatte keine Funktion mehr" (Jörg 2026-10-01).
      const current = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams(searchParams);
      setSearchParams(writeListState(schema, current, patch), { replace: true, preventScrollReset: true });
    } else {
      setLocal((prev) => writeListState(schema, prev, patch));
    }
  }, [enabled, schema, searchParams, setSearchParams]);

  const reset = useCallback(() => {
    const defaults: Record<string, unknown> = {};
    for (const name of Object.keys(schema)) defaults[name] = schema[name]!.default;
    set(defaults as Partial<ListValues<S>>);
  }, [schema, set]);

  return [values, set, { reset, isDefault: isDefaultState(schema, values) }];
}
